import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { FrontendValueConstraintRuleName } from "./spectrumFieldConstraintUtils";

/**
 * Given a particular field's list of arbitrarily sorted value constraints, finds a field's value constraint by name and
 * returns the constraint's index and value.
 *
 * @param form - The react-hook-form instance used to render the configure-field form.
 * @param valueConstraints - The list of value constraints for a particular field.
 * @param ruleName - The name of the value constraint to find.
 * @param parseValue - A function that parses the value of the constraint. Defaults to `Number.parseFloat`.
 * @returns The constraint's index and value, or `{ index: -1, value: null }` if the constraint is not found.
 */
export function findValueConstraint<TValue = number>(
  form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>,
  valueConstraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>,
  ruleName: FrontendValueConstraintRuleName,
  parseValue: (value: string) => TValue = (value) => Number.parseFloat(value) as TValue,
): {
  index: number;
  value: TValue | null;
} {
  const index = valueConstraints.findIndex((constraint) => constraint.constraint.valueConstraintRule === ruleName);
  if (index < 0) {
    return { index, value: null };
  }
  const value = form.getValues(`valueConstraints.${index}.args.0`);
  return {
    index,
    value: value == null ? null : parseValue(value),
  };
}
